import React from 'react'
import { View, ViewStyle } from 'react-native'
import { color } from '../../theme'
import { iconLevelUp } from '../../assets'
import { LevelProgress } from '../level-progress'
import { SvgIcon } from '../svg-icon'
import { Text } from '../text'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { roundNumber } from '@trivie/core'
import LinearGradient from 'react-native-linear-gradient'

export const Scoreboard = observer((props: any) => {
  const {
    levelsEnabled,
    branding,
    result,
    quizType,
    scoreboardOfQuiz,
    levelAndPoints,
    scoreboardRecallOfQuiz,
    enterpriseTier,
  } = props

  const { t } = useTranslation()

  const renderLevelUpContent = () => {
    if (scoreboardOfQuiz?.did_level_up) {
      return (
        <View style={LEVEL_UP_CONTAINER}>
          <SvgIcon size={32} src={iconLevelUp} />
          <View style={{ paddingLeft: 10, bottom: 2 }}>
            <Text
              tx="Scoreboard.youLeveledUp"
              variant="titleEmphasized"
              style={{ color: color.white }}
            />
            <Text
              tx="Scoreboard.keepItUp"
              variant="body2Medium"
              style={{ color: 'rgba(254, 254, 254, 0.5)' }}
            />
          </View>
        </View>
      )
    }
  }

  const isTopicCompleted =
    (scoreboardOfQuiz?.topic_quizzes_completed ?? 1) / (scoreboardOfQuiz?.topic_quiz_count ?? 1) ===
    1

  return (
    <>
      {enterpriseTier && (
        <View style={{ ...SCOREBOARD_CONTAINER, backgroundColor: branding.review_background }}>
          <Text
            style={{
              alignSelf: 'center',
              color: branding.review_background_text,
            }}
            variant={quizType === 'reinforcement' ? 'labelEmphasized' : 'body1Medium'}
            tx="Scoreboard.scoreboard"
          />
          {quizType !== 'reinforcement' && enterpriseTier && (
            <Text
              style={{ color: branding.review_background_text }}
              variant="labelEmphasized"
              text={result?.title}
            />
          )}
        </View>
      )}
      <View
        style={{
          ...SCOREBOARD_CONTENT,
          paddingTop: !enterpriseTier ? 12 : 36,
          borderTopRightRadius: !enterpriseTier ? 16 : 0,
          borderTopLeftRadius: !enterpriseTier ? 16 : 0,
          marginTop: !enterpriseTier ? 32 : 0,
          marginBottom: !enterpriseTier ? 0 : 20,
          backgroundColor: branding.review_background,
        }}
      >
        {isTopicCompleted && enterpriseTier && (
          <View>
            <View style={{ flexDirection: 'row', marginBottom: 10 }}>
              <Text
                style={{ color: branding.review_background_text }}
                variant="body1Emphasized"
                tx={
                  quizType === 'reinforcement' || !scoreboardOfQuiz?.topic_quizzes_completed
                    ? 'Scoreboard.overallRecall'
                    : 'Scoreboard.topicRecall'
                }
              />
              <Text
                style={{ color: branding.review_background_text }}
                variant="body1Emphasized"
                text={`${roundNumber(scoreboardRecallOfQuiz.recall_pct * 100, 0)}%`}
              />
            </View>
            <View style={PROGRESS_BAR_CONTAINER}>
              <LinearGradient
                style={{
                  ...PROGRESS_BAR_GRADIENT,
                  width: `${(scoreboardRecallOfQuiz.recall_pct ?? 0) * 100}%`,
                }}
                colors={['#52D060', '#EDB13E', '#FF7501']}
                locations={[0, 0.5, 1]}
                useAngle
                angle={-90}
                pointerEvents="none"
              />
            </View>
          </View>
        )}
        {quizType !== 'reinforcement' &&
          enterpriseTier &&
          scoreboardOfQuiz?.topic_quizzes_completed &&
          !isTopicCompleted && (
            <>
              <View style={{ flexDirection: 'row', marginBottom: 10 }}>
                <Text
                  style={{ color: branding.review_background_text }}
                  variant="body1Emphasized"
                  tx="Scoreboard.topicCompletion"
                />
                <Text
                  style={{ color: branding.review_background_text }}
                  variant="body1Emphasized"
                  text={`${scoreboardOfQuiz?.topic_quizzes_completed}/${scoreboardOfQuiz?.topic_quiz_count}`}
                />
              </View>
              <View style={CONTAINER}>
                <LinearGradient
                  style={{
                    ...GRADIENT,
                    width: `${
                      ((scoreboardOfQuiz?.topic_quizzes_completed ?? 0) /
                        (scoreboardOfQuiz?.topic_quiz_count ?? 1)) *
                      100
                    }%`,
                  }}
                  colors={['#967CFD', '#3177FF']}
                  locations={[0, 1]}
                  angle={180}
                  pointerEvents="none"
                />
              </View>
            </>
          )}
        {levelsEnabled && (
          <LevelProgress
            total_points={result?.total_points ?? 0}
            pointsGained={result?.points_gained}
            titleElement={
              <View style={{ display: 'flex', flexDirection: 'row' }}>
                <Text
                  style={{ color: branding.review_background_text }}
                  variant="body1Emphasized"
                  tx="Scoreboard.level"
                />
                <Text
                  style={{ color: branding.review_background_text }}
                  variant="body1Emphasized"
                  text={`${levelAndPoints?.user_level}`}
                />
              </View>
            }
            levelAndPoints={levelAndPoints}
            progressBackground={[branding.review_background, branding.review_background]}
            cardStyle={LEVEL_CARD}
            buttonStyle={{ backgroundColor: color.black, width: 24, height: 24, margin: 1 }}
            didLevelUp={scoreboardOfQuiz?.did_level_up || false}
          />
        )}
        {levelsEnabled &&
          levelAndPoints?.level_max_points &&
          !scoreboardOfQuiz?.did_level_up &&
          levelAndPoints?.level_max_points !== null && (
            <Text
              style={{ color: branding.review_background_text, marginTop: 20 }}
              variant="body2Medium"
              text={`+${levelAndPoints?.level_max_points - result?.total_points} ${t(
                'Profile.pointsToNextLevel',
              )}`}
            />
          )}
        {renderLevelUpContent()}
      </View>
    </>
  )
})

const SCOREBOARD_CONTENT: ViewStyle = {
  width: '100%',
  padding: 12,
  borderWidth: 3,
  borderColor: '#9098A320',
  borderRadius: 16,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  marginBottom: 20,
  position: 'relative',
  top: -36,
  paddingTop: 36,
}
const SCOREBOARD_CONTAINER: ViewStyle = {
  height: 66,
  width: '100%',
  padding: 12,
  borderWidth: 3,
  borderColor: '#9098A320',
  borderRadius: 16,
  zIndex: 999,
  marginBottom: 20,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
}
const LEVEL_UP_CONTAINER: ViewStyle = {
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: 'rgba(144, 152, 163, 0.2)',
  padding: 20,
  marginTop: 12,
  borderRadius: 20,
}
const LEVEL_CARD: ViewStyle = {
  backgroundColor: 'transparent',
  marginBottom: 0,
  padding: 0,
  paddingHorizontal: 0,
  paddingVertical: 0,
  shadowOpacity: 0,
}

const GRADIENT: ViewStyle = {
  // alignSelf: 'center',
  height: 30,
  borderRadius: 100,
}
const CONTAINER: ViewStyle = {
  backgroundColor: color.darkProgressBg,
  padding: 6,
  borderRadius: 100,
}
const PROGRESS_BAR_CONTAINER: ViewStyle = {
  alignSelf: 'center',
  width: '100%',
  marginBottom: 16,
  backgroundColor: color.darkProgressBg,
  padding: 6,
  borderRadius: 100,
}
const PROGRESS_BAR_GRADIENT: ViewStyle = {
  height: 22,
  borderRadius: 100,
}
